import React, { useEffect, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Delete, CameraAlt } from '@mui/icons-material';
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Button } from '../../../components';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../utils/functions/yupFormTypescript';
import {
  useGetPresignedURLQuery,
  useUpdateReportForFieldNumberMutation,
} from '../../../services/mVPGraphQlService';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import success from '../../../assets/images/success.png';

const createReport = () => {
  const formSchemaModal = yup.object({
    feedback: yup
      .string()
      .required('Feedback is required')
      .max(300, 'Feedback cannot exceed 300 characters'),
    images: yup
      .array()
      .min(1, 'Please capture at least 1 images')
      .required('Images are required'),
  });

  const {
    formState: { errors: err },
    handleSubmit,
    register: reg,
    watch: wat,
    reset,
  } = useFormWithSchema(formSchemaModal, {
    mode: 'onTouched',
  });

  const [charCount, setCharCount] = useState(0);

  const user = useSelector(
    (state: RootState) => state.authUser
  );
  const { data } = useGetPresignedURLQuery({
    actor: {
      actorId: user?.userId,
      actorName: user?.userName,
      actorType: user?.role,
    },
    urlCount: 4,
  });
  const [UpdateReportForFieldNumber] =
    useUpdateReportForFieldNumberMutation();

  const [showSuccess, setShowSuccess] = useState(false);

  const [images, setImages] = useState<
    {
      id: number;
      preview: string;
      contentLink: string | null;
      uploading: boolean;
      location?: {
        latitude: number;
        longitude: number;
        accuracy: number;
        timestamp: number;
      };
    }[]
  >([]);
  const [contentLinks, setContentLinks] = useState<
    string[]
  >([]);
  const [imageCounter, setImageCounter] = useState(0);

  const [isUploading, setIsUploading] = useState(false);

  const [availableIndexes, setAvailableIndexes] = useState<
    number[]
  >([]);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(
          new Error(
            'Geolocation is not supported by this browser.'
          )
        );
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp,
          });
        },
        (error) => {
          console.error(
            'Error getting location:',
            error.message
          );
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  const handleCapture = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (
      !event.target.files ||
      event.target.files.length === 0
    )
      return;

    const file = event.target.files[0];

    if (
      !data?.data?.GetPresignedURL.presignedUrlsWithLinks
        ?.length
    ) {
      console.error('No pre-signed URLs available!');
      return;
    }

    const nextIndex =
      availableIndexes.length > 0
        ? availableIndexes[0]
        : images.length;
    const urlData =
      data.data.GetPresignedURL.presignedUrlsWithLinks[
        nextIndex
      ];

    if (!urlData) {
      console.error('Not enough pre-signed URLs!');
      return;
    }

    const { presignedUrl, contentLink } = urlData;
    console.log('Uploading file:', file);

    // Create a unique ID for the image
    const newImageId = imageCounter + 1;
    setImageCounter(newImageId);

    // Create a local preview URL
    const localImageUrl = URL.createObjectURL(file);
    setAvailableIndexes((prev) =>
      prev.filter((idx) => idx !== nextIndex)
    );

    // Get location data
    let locationData = null;
    try {
      locationData = await getCurrentLocation();
      console.log('Location captured:', locationData);
    } catch (error) {
      console.error('Failed to get location:', error);
      // Continue without location if it fails
    }

    setImages((prev) => [
      ...prev,
      {
        id: newImageId,
        preview: localImageUrl,
        contentLink: null,
        uploading: true,
        location: locationData || undefined,
      },
    ]);

    try {
      setIsUploading(true);
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!response.ok) {
        throw new Error(`Upload failed for ${file.name}`);
      }

      console.log(`Upload successful: ${contentLink}`);

      setImages((prev) =>
        prev.map((img) =>
          img.id === newImageId
            ? { ...img, contentLink, uploading: false }
            : img
        )
      );

      setContentLinks((prev) => [...prev, contentLink]);

      reset({ images: [...contentLinks, contentLink] });

      // Clean up memory
      URL.revokeObjectURL(localImageUrl);
    } catch (error) {
      console.error('Image upload failed:', error);

      // Remove the failed image preview
      setImages((prev) =>
        prev.filter((img) => img.id !== newImageId)
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (id: number) => {
    setImages((prev) => {
      const removedIndex = prev.findIndex(
        (img) => img.id === id
      );

      if (removedIndex === -1) return prev; // Image not found

      // Mark the removed index as available
      setAvailableIndexes((prevIndexes) =>
        [...prevIndexes, removedIndex].sort()
      );

      return prev.filter((img) => img.id !== id);
    });

    setContentLinks((prev) =>
      prev.filter(
        (_, i) =>
          i !== images.findIndex((img) => img.id === id)
      )
    );

    reset({
      images: contentLinks.filter(
        (_, i) =>
          i !== images.findIndex((img) => img.id === id)
      ),
    });
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fieldId = searchParams.get('fieldId');
  const reportId = searchParams.get('reportId');

  const { path } = location.state || {};
  const [isSubmitDisabled, setIsSubmitDisabled] =
    useState(true);

  useEffect(() => {
    const allUploaded = images.every(
      (img) => !img.uploading
    ); // Ensure all images are uploaded

    setIsSubmitDisabled(
      isUploading || // If any image is being uploaded
        !wat('feedback') ||
        wat('feedback').length > 300 ||
        images.length < 1 || // Ensure at least one image exists
        !allUploaded // ✅ Ensures all images are uploaded before enabling submit
    );
  }, [wat('feedback'), images, isUploading]);

  const onSubmit = (data) => {
    const obj = {
      fieldNo: Number(fieldId),
      reportId:
        path == 'fromTimeSeries' ? 0 : Number(reportId),
      reportElement: {
        elementType: 'TEXT_WITH_IMAGES',
        elementValue: {
          text: data.feedback,
          contentList: images.map((img) => ({
            latitude: img.location?.latitude || 0, // Default to 0 if no location
            longitude: img.location?.longitude || 0,
            accuracy: img.location?.accuracy
              ? Math.round(img.location.accuracy)
              : 0,
            contentLink: img.contentLink || '', // Ensure a valid string
          })),
        },
        createdActorTrace: {
          actorId: {
            actorId: user?.userId,
            actorName: user?.userName,
            actorType: user?.role,
          },
          actorTime: {
            seconds: Math.floor(Date.now() / 1000),
          },
        },
      },
    };

    UpdateReportForFieldNumber(obj)
      .unwrap()
      .then((response) => {
        const statusResponse =
          response?.data?.UpdateReportForFieldNumber
            ?.responseStatus;
        if (statusResponse?.status === 'SUCCESS') {
          setShowSuccess(true);
          setTimeout(() => {
            navigate('/mvp/reports');
          }, 3000);
        }
      });
  };

  return showSuccess ? (
    <div className="flex flex-col items-center justify-center h-screen text-center">
      <img
        src={success}
        alt="No Data Found"
        style={{
          maxWidth: '100%', // Prevents overflow
          maxHeight: '100%', // Maintains aspect ratio
          objectFit: 'contain',
        }}
      />
      <h2 className="mt-4 text-2xl font-semibold">
        Success!
      </h2>
      <p className="text-gray-500">
        Your report has been recorded!
      </p>
    </div>
  ) : (
    <form onSubmit={handleSubmit((data) => onSubmit(data))}>
      <Box
        sx={{
          backgroundColor: '#d3d3d3',
          width: '100vw',
          /// height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          sx={{
            marginBottom: 3,
            backgroundColor: '#fff',
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px',
            }}>
            <IconButton
              className="close-icon-btn"
              type="button"
              onClick={() => navigate(-1)}>
              <ArrowBackIcon className="close-icon" />
            </IconButton>

            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold' }}>
              {Number(fieldId)}
            </Typography>
          </div>
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: '#fff',
            height: '86vh',
            borderRadius: 2,
            marginLeft: 2,
            marginRight: 2,
            marginBottom: 10,
          }}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 1 }}
            fontSize={13}>
            Feedback (maximum 300 characters)
          </Typography>

          <textarea
            id="feedback"
            {...reg('feedback', {
              required: 'Feedback are required',
              maxLength: {
                value: 300,
                message:
                  'Feedback cannot exceed 300 characters',
              },
            })}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              err.feedback
                ? 'border-red-500'
                : 'border-gray-300'
            }`}
            placeholder="Write feedback"
            onChange={(e) => {
              reg('feedback').onChange(e);
              setCharCount(e.target.value.length);
            }}
          />
          <div className="flex justify-between mt-1">
            <div className="text-sm text-red-500">
              {err.feedback?.message}
            </div>
            <div
              className={`text-sm ${
                charCount > 300
                  ? 'text-red-500'
                  : 'text-gray-500'
              }`}>
              {charCount}/300 characters
            </div>
          </div>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 1, mt: 2 }}
            fontSize={13}>
            Attachments (Maximum 4)
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-start', // Aligns items to the left
              flexWrap: 'wrap',
              mt: 1,
            }}>
            {/* Capture Photo Box */}
            <label htmlFor="cameraInput">
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  border: '1px dashed #aaa',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor:
                    images.length >= 4
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    images.length >= 4
                      ? 'rgba(239, 245, 246, 0.5)'
                      : 'rgba(239, 245, 246, 1)',
                  opacity: images.length >= 4 ? 0.5 : 1, // Dim when disabled
                }}>
                <IconButton
                  component="span"
                  sx={{
                    color: 'rgba(110, 161, 169, 1)',
                    pointerEvents:
                      images.length >= 4 ? 'none' : 'auto', // Disable clicks
                  }}
                  disabled={images.length >= 4}>
                  <CameraAlt />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 1,
                    color: 'rgba(110, 161, 169, 1)',
                  }}>
                  Add Photo
                </Typography>
              </Box>
            </label>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCapture}
              style={{ display: 'none' }}
              id="cameraInput"
              disabled={images.length >= 4}
            />
            {images.map((img, index) => (
              <Box
                key={img.id}
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}>
                <img
                  src={img.preview}
                  alt={`Captured ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />

                {img.uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
                    <span className="text-white">
                      Uploading...
                    </span>
                  </div>
                )}

                {!img.uploading && (
                  <IconButton
                    onClick={() =>
                      handleRemoveImage(img.id)
                    }
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.5)',
                      color: 'white',
                      p: 0.5,
                    }}
                    size="small">
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: '#fff',
            boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
            zIndex: 1000,
          }}>
          <Button
            variant="button--primary"
            style={{
              width: '100%',
              height: 60,
              opacity: isSubmitDisabled ? '30%' : '100%',
              borderRadius: 0,
            }}
            disabled={isSubmitDisabled}
            type="submit">
            Submit
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default createReport;
